import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, computed, ElementRef, forwardRef, inject, resource, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

import { MaterialSymbolIcon, MaterialSymbolsIconsService } from './material-symbols-icons.service';

const ICON_COLUMNS = 6;
const ICON_SIZE_PX = 40;

@Component({
	selector: 'dix-icon-selector',
	templateUrl: './icon-selector.component.html',
	styleUrl: './icon-selector.component.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => IconSelectorComponent),
			multi: true,
		},
	],
	imports: [
		CdkOverlayOrigin,
		CdkConnectedOverlay,
		CdkVirtualScrollViewport,
		CdkFixedSizeVirtualScroll,
		CdkVirtualForOf,
		MatFormFieldModule,
		MatInputModule,
		MatIconModule,
		MatButtonModule,
		ReactiveFormsModule,
	],
})
export class IconSelectorComponent implements ControlValueAccessor {
	private readonly iconsService = inject(MaterialSymbolsIconsService);
	private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

	readonly searchControl = new FormControl('', { nonNullable: true });
	readonly searchQuery = toSignal(this.searchControl.valueChanges, { initialValue: this.searchControl.value });
	readonly selectedIcon = signal('');
	readonly panelOpen = signal(false);
	readonly iconSizePx = ICON_SIZE_PX;
	readonly allIcons = resource({
		loader: () => this.iconsService.icons,
	});
	readonly filteredIcons = computed(() => filterIcons(this.allIcons.value() ?? [], this.searchQuery()));
	readonly iconRows = computed(() => chunkIcons(this.filteredIcons(), ICON_COLUMNS));

	private onChange: (value: string) => void = () => {};
	private onTouched: () => void = () => {};
	private disabled = false;

	writeValue(value: string): void {
		this.selectedIcon.set(value ?? '');
	}

	registerOnChange(fn: (value: string) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	togglePanel(): void {
		if (this.disabled) return;
		this.panelOpen.update((open) => !open);
		if (this.panelOpen()) {
			this.searchControl.reset();
			setTimeout(() => this.searchInput()?.nativeElement.focus());
		}
	}

	selectIcon(icon: string): void {
		this.selectedIcon.set(icon);
		this.onChange(icon);
		this.onTouched();
		this.panelOpen.set(false);
	}

	closePanel(): void {
		this.panelOpen.set(false);
		this.onTouched();
	}

	displayIconName(name: string): string {
		return name.replaceAll('_', ' ');
	}
}

function filterIcons(icons: MaterialSymbolIcon[], rawQuery: string): MaterialSymbolIcon[] {
	const query = rawQuery.toLowerCase().trim();
	if (!query) return icons;

	const matched = new Map<string, MaterialSymbolIcon>();
	addMatchingIcons(icons, query, matched);

	if (query.includes(' ')) {
		addMatchingIcons(icons, query.replaceAll(' ', '_'), matched);
	}

	return [...matched.values()];
}

function addMatchingIcons(icons: MaterialSymbolIcon[], query: string, matched: Map<string, MaterialSymbolIcon>): void {
	for (const icon of icons) {
		if (matched.has(icon.name)) continue;
		if (icon.name.includes(query) || icon.tags.some((tag) => tag.toLowerCase().includes(query))) {
			matched.set(icon.name, icon);
		}
	}
}

function chunkIcons(icons: MaterialSymbolIcon[], columns: number): MaterialSymbolIcon[][] {
	const rows: MaterialSymbolIcon[][] = [];
	for (let i = 0; i < icons.length; i += columns) {
		rows.push(icons.slice(i, i + columns));
	}
	return rows;
}
